<template>
  <div
    class="relative min-w-0 max-w-full"
    :class="fileListOffsetClass"
  >
    <div
      v-if="areAllFilesImagesOrVideo && files.length > 4"
      class="bottom-0 right-0 rounded-br-12 hidden cursor-pointer w-1/2 h-1/2 tablet:flex justify-center text-2024 font-medium items-center absolute z-10 bg-black/75 text-white"
      @click="onImageOrVideoClick(files[3]!)"
    >
      +{{ files.length - 4 }}
    </div>
    <ul
      class="relative flex flex-col"
      :class="[
        areAllFilesImagesOrVideo ? [imagesFilesGroup, imagesGrid, imagesGroupBackground] : 'gap-1',
        { 'full-bleed-media': fullBleed && areAllFilesImagesOrVideo },
      ]"
      :style="mosaicStyle"
    >
      <li
        v-for="(file, index) in files"
        :key="`file-${file.filename}`"
        :data-cookie="file.filecookie"
        class="h-full min-h-0 min-w-0 max-w-full"
      >
        <voice-message-bubble
          v-if="isVoiceEvent"
          :file-cookie="file.filecookie"
          :p-served="event?.item.pServed ?? ''"
          :event-id="event?.eventId ?? 0"
          :is-outgoing="type === 'OutgoingMessage'"
          :executor="event?.item.executor ?? ''"
          data-test="file-link-item-voice"
          @resize="emits('images-loaded')"
        />
        <file-audio
          v-else-if="isAudioFile(file.filename)"
          :size="file.filesize ?? 0"
          :src="getAuthenticatedMediaUrl(`/files/${file.filecookie}`)"
          :name="file.filename"
          :type="type"
          data-test="file-link-item-audio"
          :is-pinned="isPinned!"
          :is-group="isGroup!"
          :event="event!"
        />
        <div
          v-else-if="isImageFile(file.filename)"
          class="relative overflow-hidden"
          :class="[
            areAllFilesImagesOrVideoAndMoreThanOne || fullBleed
              ? 'h-full w-full'
              : 'w-full tablet:max-w-48',
            { 'rounded-br-12!': areAllFilesImagesOrVideo && files.length > 4 && index === 3 },
          ]"
          :style="areAllFilesImagesOrVideoAndMoreThanOne
            ? undefined
            : getImFileMediaPreviewBoxStyle(file)"
        >
          <file-image
            class="h-full w-full"
            :class="{ 'tablet:hidden block': areAllFilesImagesOrVideo && files.length > 4 && index > 3 }"
            :file="file"
            data-test="file-link-item-image"
            :type="type"
            :is-rounded="!areAllFilesImagesOrVideo"
            @img-click="onImageOrVideoClick"
            @loaded="onImageLoaded(file)"
          />
        </div>
        <div
          v-else-if="isVideoFile(file.filename)"
          class="relative flex justify-center items-center overflow-hidden"
          :class="
            areAllFilesImagesOrVideoAndMoreThanOne || fullBleed
              ? 'h-full w-full'
              : 'w-full tablet:max-w-48 tablet:aspect-4/3'
          "
          :style="areAllFilesImagesOrVideoAndMoreThanOne
            ? undefined
            : getImFileMediaPreviewBoxStyle(file, { width: 4, height: 3 })"
        >
          <chat-video-file
            :src="getAuthenticatedMediaUrl(`/files/${file.filecookie}`)"
            :file="file"
            data-test="file-link-item-video"
            :type="type"
            :is-rounded="!areAllFilesImagesOrVideo"
            class="h-full w-full"
            @on-video-click="onImageOrVideoClick"
          />
        </div>
        <div
          v-else
          class="flex items-center gap-x-2 rounded-8 p-2 transition-all"
          data-test="file-link-item-other"
          :class="fileSurfaceClass"
        >
          <div class="size-9 shrink-0">
            <file-icon
              :mime="file.filemime ?? ''"
              :cookie="file.filecookie"
              :name="file.filename"
              :event="event"
              @doc-click="onDocClick(file)"
            />
          </div>
          <div class="grid min-w-0 grow">
            <p
              class="mb-0.5 truncate text-label-m-13-18 text-fg-base-fg3"
              :title="file.filename"
            >
              {{ file.filename }}
            </p>
            <p
              v-if="getFileMetaLabel(file)"
              class="truncate text-label-r-12-18 text-fg-base-fg17"
            >
              {{ getFileMetaLabel(file) }}
            </p>
          </div>
          <download-file
            :files="file"
            :path-name="'/files/'"
          >
            <template #default="{ download }">
              <wui-icon
                name="downloadM"
                :title="$t('common.download')"
                class="size-5! shrink-0 cursor-pointer text-fg-base-fg10"
                @contextmenu.stop
                @click="download"
              />
            </template>
          </download-file>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { WuiIcon } from '@wui/common-library'
import { HistoryMessageItem, MessageOutgoingEvent, MessageIncomingEvent, IMFile, PinnedMessage } from '@wui/im'
import { computed, ref, watch } from 'vue'

import { useOpenAttachmentViewer } from '@/widgets/media-with-thread-chat'

import { ChatVideoFile } from '@/features/chat-video-file'
import { DownloadFile } from '@/features/download-file'

import { isVoiceMessage, VoiceMessageBubble } from '@/entities/voice-message'

import { bytesConverter } from '@/shared/utils/bytes-converter'
import { getImFileMediaPreviewBoxStyle, isAudioFile, isImageFile, isVideoFile } from '@/shared/utils/files'
import { getAuthenticatedMediaUrl } from '@/shared/utils/url'

import FileAudio from '../file-audio/file-audio.vue'
import FileIcon from '../file-icon/file-icon.vue'
import FileImage from '../file-image/file-image.vue'

const props = defineProps<{
  files: IMFile[],
  type: 'IncomingMessage' | 'OutgoingMessage',
  isPinned?: PinnedMessage,
  isGroup?: boolean,
  event?: HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent> | undefined,
  fullBleed?: boolean,
}>()

const emits = defineEmits(['images-loaded'])

const { openAttachmentViewer } = useOpenAttachmentViewer()

const areAllFilesImagesOrVideo = computed(() => {
  return props.files.every((file) => isImageFile(file.filename) || isVideoFile(file.filename))
})

const isVoiceEvent = computed(() => isVoiceMessage(props.event?.item))

const fileListOffsetClass = computed(() => {
  if (areAllFilesImagesOrVideo.value) return ''
  if (isVoiceEvent.value) return 'mx-2 mt-2'
  return 'm-2'
})

const onImageOrVideoClick = (file: IMFile) => {
  if (!props.event) {
    return
  }
  void openAttachmentViewer(file, props.event)
}

const onDocClick = async (file: IMFile) => {
  if (!props.event) {
    return
  }
  await openAttachmentViewer(file, props.event)
}

const areAllFilesImagesOrVideoAndMoreThanOne = computed(() => {
  return areAllFilesImagesOrVideo.value && props.files.length > 1
})

type ImageMosaicLayout = {
  minPx: number
  ratio: string
  tabletMinPx: number
  tabletRatio: string
  gridClass: string
}

const tabletGrid = 'tablet:grid-cols-[repeat(2,minmax(0,1fr))] tablet:grid-rows-[1fr_1fr]'

const IMAGE_MOSAIC_LAYOUT: Record<number, ImageMosaicLayout> = {
  2: {
    minPx: 476,
    ratio: '476 / 472',
    tabletMinPx: 248,
    tabletRatio: '248 / 192',
    gridClass: '!grid grid-cols-[minmax(0,1fr)] grid-rows-[1fr_1fr]',
  },
  3: {
    minPx: 472,
    ratio: '472 / 472',
    tabletMinPx: 244,
    tabletRatio: '244 / 192',
    gridClass: '!grid three-images grid-cols-[repeat(2,minmax(0,1fr))] grid-rows-[158fr_314fr] tablet:grid-rows-[1fr_1fr]',
  },
  4: {
    minPx: 468,
    ratio: '468 / 472',
    tabletMinPx: 244,
    tabletRatio: '244 / 240',
    gridClass: `!grid four-images grid-cols-[repeat(3,minmax(0,1fr))] grid-rows-[158fr_314fr] ${tabletGrid}`,
  },
  5: {
    minPx: 456,
    ratio: '456 / 472',
    tabletMinPx: 244,
    tabletRatio: '244 / 240',
    gridClass: `!grid five-images grid-cols-[repeat(6,minmax(0,1fr))] grid-rows-[158fr_314fr] ${tabletGrid}`,
  },
  6: {
    minPx: 472,
    ratio: '472 / 468',
    tabletMinPx: 244,
    tabletRatio: '244 / 240',
    gridClass: `!grid grid-cols-[repeat(2,minmax(0,1fr))] grid-rows-[1fr_1fr_1fr] ${tabletGrid}`,
  },
  7: {
    minPx: 456,
    ratio: '456 / 468',
    tabletMinPx: 244,
    tabletRatio: '244 / 240',
    gridClass: `!grid seven-images grid-cols-[repeat(6,minmax(0,1fr))] grid-rows-[1fr_1fr_1fr] ${tabletGrid}`,
  },
  8: {
    minPx: 456,
    ratio: '456 / 468',
    tabletMinPx: 244,
    tabletRatio: '244 / 240',
    gridClass: `!grid eight-images grid-cols-[repeat(6,minmax(0,1fr))] grid-rows-[1fr_1fr_1fr] ${tabletGrid}`,
  },
  9: {
    minPx: 468,
    ratio: '468 / 468',
    tabletMinPx: 244,
    tabletRatio: '244 / 240',
    gridClass: `!grid grid-cols-[repeat(3,minmax(0,1fr))] grid-rows-[1fr_1fr_1fr] ${tabletGrid}`,
  },
  10: {
    minPx: 432,
    ratio: '432 / 468',
    tabletMinPx: 244,
    tabletRatio: '244 / 240',
    gridClass: `!grid ten-images grid-cols-[repeat(12,minmax(0,1fr))] grid-rows-[1fr_1fr_1fr] ${tabletGrid}`,
  },
}

const imagesFilesGroup = computed(() => {
  if (areAllFilesImagesOrVideoAndMoreThanOne.value) {
    return 'image-mosaic rounded-t-12 overflow-hidden'
  }
  if (props.fullBleed) {
    return 'overflow-hidden w-full max-h-[476px]'
  }
  return 'rounded-t-12 overflow-hidden w-auto h-auto w-[248px] tablet:max-h-[249px] max-h-[476px]'
})

const mosaicStyle = computed(() => {
  const layout = IMAGE_MOSAIC_LAYOUT[props.files.length]
  if (!layout || !areAllFilesImagesOrVideoAndMoreThanOne.value) {
    return undefined
  }
  return {
    '--mosaic-min': `${layout.minPx}px`,
    '--mosaic-ratio': layout.ratio,
    '--mosaic-min-tablet': `${layout.tabletMinPx}px`,
    '--mosaic-ratio-tablet': layout.tabletRatio,
  }
})

const imagesGroupBackground = computed(() => {
  if (props.fullBleed) {
    return 'bg-transparent'
  }
  return props.type === 'OutgoingMessage'
    ? 'bg-comp-messages-bubble-bg-out-def'
    : isVoiceEvent.value ? 'bg-transparent' : 'bg-surf-bg-def'
})

const fileSurfaceClass = computed(() => 'bg-bg-base-bg1')

const getFileExtensionLabel = (file: IMFile): string => {
  const fromName = file.filename.split('.').at(-1)?.toUpperCase()
  if (fromName && fromName !== file.filename.toUpperCase()) {
    return fromName
  }
  return ''
}

const getFileMetaLabel = (file: IMFile): string => {
  const parts: string[] = []
  if (file.filesize) {
    parts.push(String(bytesConverter(file.filesize)))
  }
  const extension = getFileExtensionLabel(file)
  if (extension) {
    parts.push(extension)
  }
  return parts.join(', ')
}

const imagesGrid = computed(() => {
  if (!areAllFilesImagesOrVideo.value) {
    return ''
  }
  return IMAGE_MOSAIC_LAYOUT[props.files.length]?.gridClass ?? ''
})

const loadedImages = ref<Set<IMFile['filename']>>(new Set())
const allImagesLoaded = computed(() => loadedImages.value.size === props.files.length)
const onImageLoaded = (file: IMFile) => {
  loadedImages.value.add(file.filename)
}
watch(allImagesLoaded, () => {
  if (allImagesLoaded.value) {
    emits('images-loaded')
  }
})
</script>

<style scoped>
@reference "../../../../app/assets/tailwind.css";

.image-mosaic {
  width: 100%;
  max-width: 100%;
  min-width: min(var(--mosaic-min, 476px), calc(var(--chat-message-max-width, 53.777%) / 1% * 1cqw));
  aspect-ratio: var(--mosaic-ratio, auto);
}

@media (max-width: 1001px) {
  .image-mosaic {
    min-width: min(var(--mosaic-min-tablet, 248px), calc(var(--chat-message-max-width, 53.777%) / 1% * 1cqw));
    aspect-ratio: var(--mosaic-ratio-tablet, auto);
  }
}

.five-images li:nth-child(1),
.five-images li:nth-child(2),
.five-images li:nth-child(3),
.seven-images li:nth-child(3),
.eight-images li:nth-child(3),
.eight-images li:nth-child(6),
.seven-images li:nth-child(4),
.eight-images li:nth-child(4),
.eight-images li:nth-child(7),
.seven-images li:nth-child(5),
.eight-images li:nth-child(5),
.eight-images li:nth-child(8) {
  @apply desktop:col-span-2
}

.three-images li:nth-child(3) {
   @apply col-span-2
}

.four-images li:nth-child(4),
.five-images li:nth-child(4),
.seven-images li:nth-child(1),
.seven-images li:nth-child(6),
.eight-images li:nth-child(1),
.seven-images li:nth-child(2),
.seven-images li:nth-child(7),
.eight-images li:nth-child(2),
.five-images li:nth-child(5),
.ten-images li:nth-child(5),
.ten-images li:nth-child(4),
.ten-images li:nth-child(6),
.ten-images li:nth-child(7) {
  @apply desktop:col-span-3
}

.ten-images li:nth-child(1),
.ten-images li:nth-child(8),
.ten-images li:nth-child(2),
.ten-images li:nth-child(9),
.ten-images li:nth-child(3),
.ten-images li:nth-child(10) {
  @apply desktop:col-span-4
}

ul:not(.full-bleed-media) li:first-child,
ul:not(.full-bleed-media) li:last-child {
  border-radius: 12px;
}
</style>
